import React, { useEffect, useMemo } from 'react';
import { Box, Paper, Typography, CircularProgress, Chip, Stack } from '@mui/material';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import { usePdf } from '../../context/PdfContext';

const PdfViewer: React.FC = () => {
  const { files, currentFileId, processFile, batchInfo } = usePdf();
  
  const currentFile = files.find((file) => file.id === currentFileId);

  // Estimate page count based on text length
  const pageCount = useMemo(() => {
    if (!currentFile) return 0;
    if (currentFile.text) {
      return Math.max(1, Math.ceil(currentFile.text.length / 3000));
    }
    return 0;
  }, [currentFile]);

  // Auto-process file when selected. Skip files that already errored so a
  // failed extraction (e.g. a scanned PDF) doesn't re-trigger in a loop.
  useEffect(() => {
    if (currentFile && !currentFile.text && !currentFile.isProcessing && !currentFile.error) {
      processFile(currentFile.id);
    }
  }, [currentFile, processFile]);

  if (!currentFile) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1">No PDF file selected</Typography>
      </Box>
    );
  }

  if (currentFile.isProcessing) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <CircularProgress size={60} sx={{ mb: 2 }} />
        <Typography variant="h6" sx={{ mb: 1 }}>Processing PDF</Typography>
        <Typography variant="body1" color="text.secondary">
          Extracting text from {currentFile.file.name}...
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          This may take a moment for large files
        </Typography>
      </Box>
    );
  }

  if (currentFile.error) {
    // Categorize the error so we can show a tailored, actionable message.
    const lowerError = currentFile.error.toLowerCase();
    const isOcrError = lowerError.includes('ocr');
    const isFlattenError = lowerError.includes('flatten');
    const isNoTextError =
      lowerError.includes('no text') ||
      lowerError.includes('scanned') ||
      lowerError.includes('image-based');

    return (
      <Paper sx={{ p: 4, textAlign: 'center', minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ maxWidth: 520 }}>
          <Typography variant="h6" color={isNoTextError ? 'warning.main' : 'error'} gutterBottom>
            {isOcrError ? 'OCR Processing Error' :
             isFlattenError ? 'PDF Flattening Error' :
             isNoTextError ? 'No Selectable Text Found' :
             'Error Processing Document'}
          </Typography>
          <Typography variant="body1" gutterBottom>
            {currentFile.error}
          </Typography>
          {isNoTextError && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              This looks like a scanned or image-only PDF — it contains pictures of text
              rather than selectable text, so there is nothing to extract. Converting it to
              text would require OCR (optical character recognition), which this tool does
              not currently perform. Try a text-based PDF, or run the file through an OCR
              tool first. Processing has stopped — no further attempts will be made on this file.
            </Typography>
          )}
          {isOcrError && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Try processing without OCR enabled or check if the PDF contains valid images.
              Some scanned documents may not be compatible with browser-based OCR.
            </Typography>
          )}
          {isFlattenError && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              PDF flattening may not work with all PDF types. Try with a different PDF or use the text extraction feature instead.
              Some complex PDFs with advanced features may not be compatible with browser-based flattening.
            </Typography>
          )}
        </Box>
      </Paper>
    );
  }

  // If we have batch info and multiple files are selected, show batch view
  if (batchInfo && files.filter(f => f.selected).length > 1) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <FolderZipIcon color="primary" />
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Batch Selection
          </Typography>
          <Chip 
            label={`${batchInfo.totalFiles} files selected`} 
            color="primary" 
            size="small" 
            variant="outlined" 
          />
        </Stack>
        
        <Typography variant="body1" gutterBottom>
          {batchInfo.combinedName}
        </Typography>
        
        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
          <Typography variant="body2" color="textSecondary">
            {batchInfo.processedFiles} of {batchInfo.totalFiles} files processed
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Approximately {batchInfo.totalPages} page{batchInfo.totalPages !== 1 ? 's' : ''} total
          </Typography>
        </Stack>
      </Paper>
    );
  }
  
  // Single file view
  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        {currentFile.file.name}
      </Typography>
      <Typography variant="body2" color="textSecondary">
        {pageCount} page{pageCount !== 1 ? 's' : ''}
      </Typography>
    </Paper>
  );
};

export default PdfViewer;
